/**
 * Fuzzy Search Fallback Module
 * Fallback механизм: если основной поиск дал 0 результатов, запускается нечеткий поиск через uFuzzy + Fuse.js
 */

// Кэш для результатов fallback (чтобы не пересчитывать для одинаковых запросов)
const fallbackCache = new Map();
const FALLBACK_CACHE_TTL = 5000; // 5 секунд

// Debounce для fallback (чтобы не запускать при каждом изменении запроса)
// Fuse fallback использует более длинный debounce (700ms) для экономии ресурсов
let fallbackDebounceTimeout = null;
const FALLBACK_DEBOUNCE_MS = 700; // 700ms задержка для Fuse fallback (медленный поиск)

// Минимальная длина запроса для запуска fallback
const MIN_QUERY_LENGTH = 3;

// Текущий запрос, ожидающий fallback
let pendingFallbackQuery = null;
let pendingFallbackFiles = null;
let pendingFallbackOptions = null;

/**
 * Запланировать выполнение fuzzy fallback поиска с debounce
 * Fallback запускается только если прошло 700ms БЕЗ новых вводов
 * @param {string} query - Поисковый запрос
 * @param {Array} filteredFiles - Уже отфильтрованные файлы (после категорий, дат, publicOnly)
 * @param {Object} options - Опции поиска
 * @param {boolean} debugMode - Режим отладки
 * @returns {void}
 */
export function scheduleFuzzyFallback(query, filteredFiles, options = {}, debugMode = '0') {
    // Нечеткий поиск временно отключен (законсервирован)
    return;
    
    // Проверка минимальной длины запроса
    if (!query || query.trim().length < MIN_QUERY_LENGTH) {
        if (debugMode === '1') {
            console.log('[FuzzyFallback] Query too short, cancelling fallback:', { query, minLength: MIN_QUERY_LENGTH });
        }
        // Отменить запланированный fallback
        if (fallbackDebounceTimeout) {
            clearTimeout(fallbackDebounceTimeout);
            fallbackDebounceTimeout = null;
        }
        pendingFallbackQuery = null;
        return;
    }
    
    // Сохранить параметры для выполнения
    pendingFallbackQuery = query.trim();
    pendingFallbackFiles = filteredFiles;
    pendingFallbackOptions = options;
    
    // Отменить предыдущий таймер (если был)
    if (fallbackDebounceTimeout) {
        if (debugMode === '1') {
            console.log('[FuzzyFallback] Resetting debounce timer (new input detected):', {
                previousQuery: pendingFallbackQuery,
                newQuery: query.trim(),
                debounceMs: FALLBACK_DEBOUNCE_MS
            });
        }
        clearTimeout(fallbackDebounceTimeout);
    }
    
    // Запланировать выполнение fallback через 700ms
    fallbackDebounceTimeout = setTimeout(() => {
        const queryToProcess = pendingFallbackQuery;
        const filesToProcess = pendingFallbackFiles;
        const optionsToProcess = pendingFallbackOptions;
        
        if (debugMode === '1') {
            console.log('[FuzzyFallback] Debounce period completed, executing fallback:', {
                query: queryToProcess,
                filesCount: filesToProcess?.length || 0
            });
        }
        
        // Очистить состояние перед выполнением (чтобы новый ввод мог отменить)
        fallbackDebounceTimeout = null;
        pendingFallbackQuery = null;
        pendingFallbackFiles = null;
        pendingFallbackOptions = null;
        
        // Выполнить fallback
        const results = performFuzzyFallbackInternal(
            queryToProcess, 
            filesToProcess, 
            optionsToProcess, 
            debugMode
        );
        
        // Уведомить о результатах через событие
        if (results.length > 0) {
            // Отправить событие для обновления результатов
            window.dispatchEvent(new CustomEvent('fuzzyFallbackCompleted', {
                detail: {
                    query: queryToProcess,
                    results: results,
                    resultsCount: results.length
                }
            }));
            
            if (debugMode === '1') {
                console.log('[FuzzyFallback] Fallback completed, results dispatched:', {
                    query: queryToProcess,
                    resultsCount: results.length
                });
            }
        } else if (debugMode === '1') {
            console.log('[FuzzyFallback] Fallback returned no results');
        }
    }, FALLBACK_DEBOUNCE_MS);
    
    if (debugMode === '1') {
        console.log('[FuzzyFallback] Scheduled fallback with debounce:', {
            query: query.trim(),
            debounceMs: FALLBACK_DEBOUNCE_MS,
            willExecuteIn: `${FALLBACK_DEBOUNCE_MS}ms if no new input`
        });
    }
}

/**
 * Внутренняя функция выполнения fuzzy fallback поиска
 * @param {string} query - Поисковый запрос
 * @param {Array} filteredFiles - Уже отфильтрованные файлы (после категорий, дат, publicOnly)
 * @param {Object} options - Опции поиска
 * @param {boolean} debugMode - Режим отладки
 * @returns {Array} Массив найденных файлов или пустой массив
 */
function performFuzzyFallbackInternal(query, filteredFiles, options = {}, debugMode = '0') {
    // Проверка минимальной длины запроса
    if (!query || query.trim().length < MIN_QUERY_LENGTH) {
        if (debugMode === '1') {
            console.log('[FuzzyFallback] Query too short:', { query, minLength: MIN_QUERY_LENGTH });
        }
        return [];
    }
    
    const queryTrimmed = query.trim();
    
    // Проверка кэша
    const cacheKey = `${queryTrimmed}_${filteredFiles.length}`;
    const cached = fallbackCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < FALLBACK_CACHE_TTL) {
        if (debugMode === '1') {
            console.log('[FuzzyFallback] Using cached result:', { query: queryTrimmed, resultsCount: cached.results.length });
        }
        return cached.results;
    }
    const fuzzyApi = (window.fuzzySearch || window.FuzzySearch);
    
    if (!fuzzyApi || typeof fuzzyApi.rankItems !== 'function') {
        if (debugMode === '1') {
            console.log('[FuzzyFallback] API not available');
        }
        return [];
    }
    
    if (!filteredFiles || filteredFiles.length === 0) {
        if (debugMode === '1') {
            console.log('[FuzzyFallback] Skipped: no files');
        }
        return [];
    }
    
    // Ограничить количество файлов для обработки (fallback не должен быть слишком медленным)
    // Можно изменить через options.maxFilesForFallback или использовать значение по умолчанию
    const MAX_FILES_FOR_FALLBACK = options.maxFilesForFallback !== undefined ? options.maxFilesForFallback : 10000;
    const filesToProcess = filteredFiles.length > MAX_FILES_FOR_FALLBACK 
        ? filteredFiles.slice(0, MAX_FILES_FOR_FALLBACK)
        : filteredFiles;
    
    if (filteredFiles.length > MAX_FILES_FOR_FALLBACK && debugMode === '1') {
        console.log('[FuzzyFallback] Limiting files for performance:', {
            total: filteredFiles.length,
            processing: filesToProcess.length,
            maxFilesForFallback: MAX_FILES_FOR_FALLBACK
        });
    }
    
    if (debugMode === '1') {
        console.log('[FuzzyFallback] Starting fallback search:', {
            query,
            filteredFilesCount: filteredFiles.length
        });
    }
    
    // Подготовить файлы с fullPath для поиска
    const filesWithFullPath = filesToProcess.map(item => {
        const path = item.p || item.path || '';
        const name = item.n || item.name || '';
        const normPath = String(path).replace(/\\/g, '/');
        const fullPath = normPath ? `/${normPath}/${name}` : name;
        return { ...item, fullPath };
    });
    
    // uFuzzy ранжирование
    const ufuzzyRanked = fuzzyApi.rankItems(query, filesWithFullPath, {
        keys: ['fullPath', 'name', 'path', 'n', 'p'],
        threshold: 0.3
    });
    
    // Фильтровать только совпадения (score > 0)
    const ufuzzyMatches = ufuzzyRanked.filter(r => r.score > 0);
    
    if (debugMode === '1') {
        console.log('[FuzzyFallback] uFuzzy results:', {
            totalRanked: ufuzzyRanked.length,
            matched: ufuzzyMatches.length,
            top10: ufuzzyMatches.slice(0, 10).map(r => ({
                name: r.item?.n || r.item?.name,
                score: r.score,
                fullPath: r.item?.fullPath
            }))
        });
    }
    
    if (ufuzzyMatches.length === 0) {
        return [];
    }
    
        // Применить Fuse.js сортировку с весами (имя файла важнее пути)
        if (fuzzyApi.rankItemsFuse) {
            // Веса: имя файла (n, name) важнее пути (p, path, fullPath)
            // Если найдено и в имени и в пути - будет бонус через комбинированный score
            const fuseRanked = fuzzyApi.rankItemsFuse(query, filesWithFullPath, {
                keys: ['fullPath', 'name', 'path', 'n', 'p'],
                weights: {
                    'n': 0.5,        // Имя файла - высокий приоритет
                    'name': 0.5,      // Имя файла - высокий приоритет
                    'fullPath': 0.2,  // Полный путь - низкий приоритет
                    'path': 0.15,     // Путь - низкий приоритет
                    'p': 0.15         // Путь - низкий приоритет
                },
                threshold: 0.3,
                includeMatches: true  // Включить matches для подсветки через Fuse.js
            });
        
        // Фильтровать только совпадения (score > 0) и отсортировать
        const fuseMatches = fuseRanked
            .filter(r => r.score > 0)
            .sort((a, b) => b.score - a.score);
        
        if (debugMode === '1' && fuseMatches.length > 0) {
            console.log('[FuzzyFallback] Fuse.js ranked results (first 3):', {
                sample: fuseMatches.slice(0, 3).map(r => ({
                    name: r.item?.n || r.item?.name,
                    score: r.score,
                    hasMatches: !!r.matches,
                    matchesCount: r.matches?.length || 0,
                    matchesKeys: r.matches?.map(m => m.key) || []
                }))
            });
        }
        
        // Улучшить score для файлов, где найдено и в имени и в пути
        const normalizedQuery = query.toLowerCase().replace(/[\.\-\/\s]/g, '');
        const enhancedMatches = fuseMatches.map(r => {
            const item = r.item;
            const name = (item.n || item.name || '').toLowerCase();
            const path = (item.p || item.path || '').toLowerCase();
            const fullPath = (item.fullPath || '').toLowerCase();
            const queryLower = query.toLowerCase();
            
            // Нормализованные версии для поиска без разделителей
            const normalizedName = name.replace(/[\.\-\/\s]/g, '');
            const normalizedPath = path.replace(/[\.\-\/\s]/g, '');
            const normalizedFullPath = fullPath.replace(/[\.\-\/\s]/g, '');
            
            // Проверка: найдено ли в имени (с учетом нормализации)
            const foundInName = name.includes(queryLower) || normalizedName.includes(normalizedQuery);
            
            // Проверка: найдено ли в пути (с учетом нормализации)
            const foundInPath = path.includes(queryLower) || 
                              fullPath.includes(queryLower) ||
                              normalizedPath.includes(normalizedQuery) ||
                              normalizedFullPath.includes(normalizedQuery);
            
            // Бонус: если найдено и в имени и в пути
            let bonusScore = r.score;
            if (foundInName && foundInPath) {
                // Бонус 30% к score за совпадение в обоих местах
                bonusScore = r.score * 1.3;
            } else if (foundInName) {
                // Бонус 15% за совпадение только в имени
                bonusScore = r.score * 1.15;
            }
            
            // ВАЖНО: Сохранить matches из r (если есть)
            const enhanced = {
                ...r,
                score: Math.min(bonusScore, 1000), // Ограничить максимумом
                _foundInName: foundInName,
                _foundInPath: foundInPath
            };
            
            // Сохранить matches если они есть
            if (r.matches) {
                enhanced.matches = r.matches;
            }
            
            return enhanced;
        });
        
        // Пересортировать с учетом бонусов
        enhancedMatches.sort((a, b) => b.score - a.score);
        
        if (debugMode === '1') {
            console.log('[FuzzyFallback] Fuse.js results:', {
                ufuzzyMatches: ufuzzyMatches.length,
                fuseMatches: fuseMatches.length,
                enhancedMatches: enhancedMatches.length,
                top3WithMatches: enhancedMatches.slice(0, 3).map(r => ({
                    name: r.item?.n || r.item?.name,
                    score: r.score,
                    hasMatches: !!r.matches,
                    matchesCount: r.matches?.length || 0
                }))
            });
        }
        
        const results = enhancedMatches.map(r => {
            // Пометить результаты как из fallback (для проверки в обработчике события)
            const item = { ...r.item, _fromFallback: true };
            // Сохранить Fuse.js matches для подсветки
            if (r.matches) {
                item._fuseMatches = r.matches;
                item._fuseQuery = query;
                if (debugMode === '1' && enhancedMatches.indexOf(r) < 3) {
                    console.log('[FuzzyFallback] Saving matches to item:', {
                        name: item.n || item.name,
                        hasMatches: !!r.matches,
                        matchesCount: r.matches.length,
                        matchesKeys: r.matches.map(m => m.key)
                    });
                }
            }
            return item;
        });
        
        // Сохранить в кэш
        fallbackCache.set(cacheKey, {
            results: results,
            timestamp: Date.now()
        });
        
        // Очистить старые записи из кэша (старше TTL)
        const now = Date.now();
        for (const [key, value] of fallbackCache.entries()) {
            if (now - value.timestamp > FALLBACK_CACHE_TTL) {
                fallbackCache.delete(key);
            }
        }
        
        return results;
    } else {
        // Если Fuse.js недоступен, использовать только uFuzzy результаты
        const results = ufuzzyMatches.map(r => r.item);
        
        // Сохранить в кэш
        fallbackCache.set(cacheKey, {
            results: results,
            timestamp: Date.now()
        });
        
        return results;
    }
}

/**
 * Отменить запланированный fallback
 * Вызывается когда:
 * 1. Пользователь продолжает ввод (новый поиск запущен)
 * 2. Обычный поиск нашел результаты (output.length > 0)
 * 3. Запрос стал пустым
 * @param {boolean} debugMode - Режим отладки
 * @param {string} reason - Причина отмены (для логирования)
 */
export function cancelScheduledFallback(debugMode = '0', reason = '') {
    if (fallbackDebounceTimeout) {
        if (debugMode === '1') {
            console.log('[FuzzyFallback] Cancelling scheduled fallback:', {
                reason: reason || 'user continued typing or regular search found results',
                pendingQuery: pendingFallbackQuery,
                note: 'Fallback will not execute - no longer needed'
            });
        }
        clearTimeout(fallbackDebounceTimeout);
        fallbackDebounceTimeout = null;
        pendingFallbackQuery = null;
        pendingFallbackFiles = null;
        pendingFallbackOptions = null;
    } else if (debugMode === '1' && (pendingFallbackQuery || pendingFallbackFiles)) {
        // Очистить состояние даже если таймер уже не активен
        if (debugMode === '1') {
            console.log('[FuzzyFallback] Clearing pending fallback state:', {
                reason: reason || 'cleanup',
                pendingQuery: pendingFallbackQuery
            });
        }
        pendingFallbackQuery = null;
        pendingFallbackFiles = null;
        pendingFallbackOptions = null;
    }
}

/**
 * Очистить кэш fallback (для тестирования или при необходимости)
 */
export function clearFallbackCache() {
    fallbackCache.clear();
}

/**
 * Получить результаты fallback синхронно (для обратной совместимости)
 * Используется только если scheduleFuzzyFallback недоступен
 * @deprecated Используйте scheduleFuzzyFallback для правильного debounce
 */
export function performFuzzyFallback(query, filteredFiles, options = {}, debugMode = '0') {
    return performFuzzyFallbackInternal(query, filteredFiles, options, debugMode);
}

